import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Money } from '@rental/shared';
import { api } from '../lib/api.js';
import { Badge, Button, Card, Empty, Field, Page, Table, TextInput } from '../components/ui.jsx';

interface Property {
  id: string;
  name: string;
  type: string | null;
  address: string | null;
  isActive: boolean;
}
interface Unit {
  id: string;
  kind: 'flat' | 'room' | 'shop' | 'other';
  flatNo: string | null;
  roomNo: string | null;
  floor: number | null;
  monthlyRent: number; // paise
  occupancy: 'occupied' | 'vacant' | 'maintenance' | 'reserved';
}
interface Building {
  id: string;
  name: string;
  floors: number | null;
}

export function PropertyDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();

  const propertyQ = useQuery({
    queryKey: ['property', id],
    queryFn: () => api<{ property: Property }>(`/api/v1/properties/${id}`),
    enabled: !!id,
  });
  const buildingsQ = useQuery({
    queryKey: ['property', id, 'buildings'],
    queryFn: () => api<{ buildings: Building[] }>(`/api/v1/properties/${id}/buildings`),
    enabled: !!id,
  });
  const unitsQ = useQuery({
    queryKey: ['property', id, 'units'],
    queryFn: () => api<{ units: Unit[] }>(`/api/v1/properties/${id}/units`),
    enabled: !!id,
  });

  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: ['property', id, 'buildings'] });
    void qc.invalidateQueries({ queryKey: ['property', id, 'units'] });
  };

  if (propertyQ.isLoading) return <Page title="Loading…"><Empty>…</Empty></Page>;
  if (!propertyQ.data) return <Page title="Not found"><Empty>Property not found.</Empty></Page>;

  const p = propertyQ.data.property;

  return (
    <Page
      title={p.name}
      actions={p.isActive ? <Badge tone="good">Active</Badge> : <Badge>Inactive</Badge>}
    >
      <Card className="mb-4">
        <dl className="grid gap-2 text-sm md:grid-cols-3">
          <div><dt className="text-slate-500">Type</dt><dd>{p.type ?? '—'}</dd></div>
          <div><dt className="text-slate-500">Address</dt><dd>{p.address ?? '—'}</dd></div>
        </dl>
      </Card>

      <section className="mb-6">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-lg font-medium">Buildings</h2>
        </div>
        <BuildingForm propertyId={p.id} onSaved={invalidateAll} />
        <div className="mt-3">
          {!buildingsQ.data?.buildings.length ? (
            <Empty>No buildings yet. Add one above, or skip — units can live directly under the property.</Empty>
          ) : (
            <Table>
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="border-b px-3 py-2 dark:border-slate-700">Name</th>
                  <th className="border-b px-3 py-2 dark:border-slate-700">Floors</th>
                </tr>
              </thead>
              <tbody>
                {buildingsQ.data.buildings.map((b) => (
                  <tr key={b.id} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="px-3 py-2">{b.name}</td>
                    <td className="px-3 py-2 text-slate-500">{b.floors ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-lg font-medium">Units</h2>
        </div>
        <UnitForm propertyId={p.id} buildings={buildingsQ.data?.buildings ?? []} onSaved={invalidateAll} />
        <div className="mt-3">
          {!unitsQ.data?.units.length ? (
            <Empty>No units yet.</Empty>
          ) : (
            <Table>
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="border-b px-3 py-2 dark:border-slate-700">Kind</th>
                  <th className="border-b px-3 py-2 dark:border-slate-700">Flat / Room</th>
                  <th className="border-b px-3 py-2 dark:border-slate-700">Floor</th>
                  <th className="border-b px-3 py-2 dark:border-slate-700">Monthly rent</th>
                  <th className="border-b px-3 py-2 dark:border-slate-700">Occupancy</th>
                </tr>
              </thead>
              <tbody>
                {unitsQ.data.units.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="px-3 py-2 capitalize">{u.kind}</td>
                    <td className="px-3 py-2">
                      {[u.flatNo, u.roomNo].filter(Boolean).join(' / ') || '—'}
                    </td>
                    <td className="px-3 py-2 text-slate-500">{u.floor ?? '—'}</td>
                    <td className="px-3 py-2">{Money.fromPaise(u.monthlyRent).format()}</td>
                    <td className="px-3 py-2">
                      <OccupancyBadge value={u.occupancy} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </section>
    </Page>
  );
}

function OccupancyBadge({ value }: { value: Unit['occupancy'] }) {
  const tone = value === 'occupied' ? 'good' : value === 'vacant' ? 'warn' : 'neutral';
  return <Badge tone={tone}>{value}</Badge>;
}

function BuildingForm({ propertyId, onSaved }: { propertyId: string; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [floors, setFloors] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (body: { name: string; floors?: number }) =>
      api(`/api/v1/properties/${propertyId}/buildings`, { method: 'POST', body }),
    onSuccess: () => { setName(''); setFloors(''); onSaved(); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });
  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({ name, floors: floors ? Number(floors) : undefined });
  }
  return (
    <Card>
      <form onSubmit={onSubmit} className="grid gap-3 md:grid-cols-4 md:items-end">
        <div className="md:col-span-2">
          <Field label="Building name">
            <TextInput required value={name} onChange={(e) => setName(e.target.value)} placeholder="Block A" />
          </Field>
        </div>
        <Field label="Floors">
          <TextInput type="number" min={0} value={floors} onChange={(e) => setFloors(e.target.value)} />
        </Field>
        <div className="flex items-center justify-end gap-2">
          {error && <span className="text-xs text-red-600">{error}</span>}
          <Button type="submit" disabled={mutation.isPending}>Add</Button>
        </div>
      </form>
    </Card>
  );
}

function UnitForm({
  propertyId, buildings, onSaved,
}: { propertyId: string; buildings: Building[]; onSaved: () => void }) {
  const [kind, setKind] = useState<Unit['kind']>('flat');
  const [flatNo, setFlatNo] = useState('');
  const [roomNo, setRoomNo] = useState('');
  const [floor, setFloor] = useState('');
  const [rent, setRent] = useState('');
  const [buildingId, setBuildingId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/api/v1/properties/${propertyId}/units`, { method: 'POST', body }),
    onSuccess: () => {
      setFlatNo(''); setRoomNo(''); setFloor(''); setRent(''); setBuildingId('');
      onSaved();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const paise = rent ? Money.parse(rent).toPaise() : 0;
      mutation.mutate({
        kind,
        flatNo: flatNo || undefined,
        roomNo: roomNo || undefined,
        floor: floor ? Number(floor) : undefined,
        monthlyRent: paise,
        buildingId: buildingId || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid amount');
    }
  }

  return (
    <Card>
      <form onSubmit={onSubmit} className="grid gap-3 md:grid-cols-6 md:items-end">
        <Field label="Kind">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as Unit['kind'])}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
          >
            <option value="flat">Flat</option>
            <option value="room">Room</option>
            <option value="shop">Shop</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="Flat #"><TextInput value={flatNo} onChange={(e) => setFlatNo(e.target.value)} /></Field>
        <Field label="Room #"><TextInput value={roomNo} onChange={(e) => setRoomNo(e.target.value)} /></Field>
        <Field label="Floor"><TextInput type="number" value={floor} onChange={(e) => setFloor(e.target.value)} /></Field>
        <Field label="Rent (₹/month)"><TextInput value={rent} onChange={(e) => setRent(e.target.value)} placeholder="15000" /></Field>
        <Field label="Building">
          <select
            value={buildingId}
            onChange={(e) => setBuildingId(e.target.value)}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
          >
            <option value="">— none —</option>
            {buildings.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
          </select>
        </Field>
        <div className="md:col-span-6 flex items-center justify-end gap-2">
          {error && <span className="text-xs text-red-600">{error}</span>}
          <Button type="submit" disabled={mutation.isPending}>Add unit</Button>
        </div>
      </form>
    </Card>
  );
}
